import React, { useState } from 'react';
import { StreamingChatWidget } from '../components/chat/StreamingChatWidget';
import { ChatWidget } from '../components/chat/ChatWidget';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Sparkles, Zap } from 'lucide-react';

export default function StreamingChatTest() {
  const [useStreaming, setUseStreaming] = useState(true);
  const [sessionId] = useState(`test-session-${Date.now()}`);
  const [visitorId] = useState(`visitor-${Math.random().toString(36).substr(2, 9)}`);
  
  // Sample page content for testing
  const pageContent = `
    Welcome to our amazing toy store! We have the best selection of toys for children of all ages.
    Our featured products include educational toys, board games, action figures, and creative play sets.
    We offer free shipping on orders over $50 and have a 30-day return policy.
    Visit our store today and discover the perfect toy for your child!
  `;
  
  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Streaming Chat Test</h1>
        <p className="text-muted-foreground">
          Test the new streaming chat functionality with real-time response updates
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle>Configuration</CardTitle>
              <CardDescription>
                Toggle between streaming and regular chat modes
              </CardDescription>
            </div>
            <Badge variant={useStreaming ? "default" : "secondary"} className="flex items-center gap-1">
              {useStreaming ? <Zap className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
              {useStreaming ? 'Streaming Mode' : 'Regular Mode'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2">
            <Switch
              id="streaming-mode"
              checked={useStreaming}
              onCheckedChange={setUseStreaming}
            />
            <Label htmlFor="streaming-mode">
              Enable Streaming Responses
            </Label>
          </div>
          <div className="mt-4 text-sm text-muted-foreground">
            <p>
              <strong>Streaming Mode:</strong> Responses appear word-by-word in real-time as they're generated
            </p>
            <p>
              <strong>Regular Mode:</strong> Complete response appears all at once after processing
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Interactive Chat Widget</CardTitle>
          <CardDescription>
            Try asking questions about the toy store to see {useStreaming ? 'streaming' : 'regular'} responses
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[600px]">
            {useStreaming ? (
              <StreamingChatWidget
                apiEndpoint="/api/chatbot"
                streamingEndpoint="/api/chatbot/stream"
                siteId="3615b17f-bf81-4252-afdd-9931d1c829e2"
                sessionId={sessionId}
                visitorId={visitorId}
                pageContent={pageContent}
                pageUrl="https://toys.example.com/test"
                position="embedded"
                useStreaming={true}
                welcomeMessage="Welcome to the Toy Store! I can help you find the perfect toy. Try asking me about our products, shipping, or return policy!"
              />
            ) : (
              <ChatWidget
                apiEndpoint="/api/chatbot"
                siteId="3615b17f-bf81-4252-afdd-9931d1c829e2"
                sessionId={sessionId}
                visitorId={visitorId}
                pageContent={pageContent}
                pageUrl="https://toys.example.com/test"
                position="embedded"
                welcomeMessage="Welcome to the Toy Store! I can help you find the perfect toy. Try asking me about our products, shipping, or return policy!"
              />
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Test Questions</CardTitle>
          <CardDescription>
            Try these sample questions to test the chat functionality
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start">
              <span className="text-muted-foreground mr-2">•</span>
              <span>What types of toys do you sell?</span>
            </li>
            <li className="flex items-start">
              <span className="text-muted-foreground mr-2">•</span>
              <span>Do you offer free shipping?</span>
            </li>
            <li className="flex items-start">
              <span className="text-muted-foreground mr-2">•</span>
              <span>What's your return policy?</span>
            </li>
            <li className="flex items-start">
              <span className="text-muted-foreground mr-2">•</span>
              <span>Can you recommend educational toys for a 5-year-old?</span>
            </li>
            <li className="flex items-start">
              <span className="text-muted-foreground mr-2">•</span>
              <span>What are your most popular board games?</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}